
import {defineComponent} from "vue";
import {TextWithTitleContentRestDtoModel} from "@/models/api/pharma-cms-content-block/TextWithTitleContentRestDtoModel";
import {CodeDescriptionRestDto, TitleMetaDataRestDto,} from "@/api/pharma-cms-content-block/models";
import {CbStructureMixin} from "@/components/layouts/content-pages/wysiwyg/structures/CbStructureMixin";
import {NormalContentBlockRestService} from "@/services/rest/cms-content-block/NormalContentBlockRestService";
import {FindNormalContentBlockPayload} from "@/models/payload/FindNormalContentBlockPayload";
import {CreateNormalContentBlockPayload} from "@/models/payload/CreateNormalContentBlockPayload";
import {SaveNormalContentBlockPayload} from "@/models/payload/SaveNormalContentBlockPayload";
import {EnumClassNameCmsEnum} from "@/models/enum/EnumClassNameCmsEnum";
import {ContentBlockTypesEnum} from "@/helpers/functions/enum";
import {CopyCpAbstractPayload} from "@/models/payload/CopyCpAbstractPayload";
import BaseSpinner from "@/components/UI/Bootstrap/BaseSpinner.vue";
import InputSelect from "@/components/UI/InputSelect.vue";
import InputText from "@/components/UI/InputText.vue";
import InputTextEditor from "@/components/UI/InputTextEditor.vue";
import AdvancedPanelSwitcher from "@/components/UI/AdvancedPanelSwitcher.vue";
import AdvancedPanel from "@/components/UI/AdvancedPanel.vue";
import TitleMetaInputComponent from "@/components/UI/TitleMetaInputComponent.vue";
import {TitleMetaDataRestDtoModel} from "@/models/api/pharma-cms-content-block/TitleMetaDataRestDtoModel";
import { EnumRestService } from "@/services/rest/enum/EnumRestService";
import {DpModuleEnum} from "@/api/enums/dp-module-enum";

export default defineComponent({
  name: "CbTextWithTitle",
  components: {TitleMetaInputComponent, AdvancedPanel, AdvancedPanelSwitcher, InputTextEditor, InputText, InputSelect, BaseSpinner},
  extends: CbStructureMixin,
  data: () => ({
    contentBlock: TextWithTitleContentRestDtoModel.createWithDefaults(),
    isAdvancedTitleMeta: false,
    enumTitleSizeCode: [] as CodeDescriptionRestDto[]
  }),
  watch: {
    contentBlock() {
      this.onCopyCpAbstractChanged();
    },
  },
  mounted() {
    this.initialize();
  },
  computed: {
    isPageTitle(): boolean {
      return this.cbType === ContentBlockTypesEnum.PAGE_TITLE;
    },
  },
  methods: {
    async find(payload: FindNormalContentBlockPayload): Promise<TextWithTitleContentRestDtoModel> {
      return await NormalContentBlockRestService.getInstance().findTextWithTitle(payload.type, payload.id);
    },
    async create(payload: CreateNormalContentBlockPayload): Promise<string> {
      return await NormalContentBlockRestService.getInstance().createTextWithTitle(payload.cb as TextWithTitleContentRestDtoModel, payload.type);
    },
    async save(payload: SaveNormalContentBlockPayload): Promise<void> {
      await NormalContentBlockRestService.getInstance().saveTextWithTitle(payload.cb as TextWithTitleContentRestDtoModel, payload.type, payload.id);
    },
    async initialize(): Promise<void> {
      this.enumTitleSizeCode = await EnumRestService.getInstance().getEnumValuesByClassName(EnumClassNameCmsEnum.TITLE_SIZE, DpModuleEnum.CMS);
    },
    getDefaultCbModel(): TextWithTitleContentRestDtoModel {
      return TextWithTitleContentRestDtoModel.createWithDefaults();
    },
    onCopyCpAbstractChanged(): void {
      const title = this.contentBlock.title?.nl ?? null;
      const content = this.contentBlock.content?.nl ?? null;
      this.copyCpAbstractPayload = new CopyCpAbstractPayload(content, null, title);

      this.$emit('abstractContentChanged', this.copyCpAbstractPayload);
    },
    toggleAdvancedTitleMeta(): void {
      if (!this.isAdvancedTitleMeta && (this.contentBlock.title_meta === null || this.contentBlock.title_meta === undefined)) {
        this.contentBlock.title_meta = TitleMetaDataRestDtoModel.createWithDefaults();
      }
      this.isAdvancedTitleMeta = !this.isAdvancedTitleMeta;
    },
    onTitleMetaUpdate(payload: TitleMetaDataRestDto): void {
      this.contentBlock.title_meta = payload;
      this.onChange();
    },
    async afterReloadContent(): Promise<void> {
      if (this.contentBlock.title_meta) {
        this.isAdvancedTitleMeta = true;
      }
    },
    async beforeSubmitData(): Promise<void> {
      if (this.contentBlock !== null && this.contentBlock.title_meta !== null && this.contentBlock.title_meta !== undefined
        && this.contentBlock.title_meta.font_size_in_rem === null
        && this.contentBlock.title_meta.font_weight_code === null
        && this.contentBlock.title_meta.text_horizontal_position_code === null
        && this.contentBlock.title_meta.text_color_code === null
        && this.contentBlock.title_meta.text_color === null) {
        this.contentBlock.title_meta = null;
      }
    },
    pasteCbMetadata(): void {
      // Reassigning contentBlock (instead of mutating it) is what fires the contentBlock watcher.
      const cbClone = TextWithTitleContentRestDtoModel.cloneFrom(this.contentBlock);

      cbClone.cloneMetadataFrom(this.getClipboardContentBlock(this.cbType) as TextWithTitleContentRestDtoModel);
      this.contentBlock = cbClone;
    },
  }
});
